/**
 * Conversation-session recording hook.
 *
 * Phase 3a — wraps the tauri-plugin-voice start_session / stop_session
 * commands. One session active at a time; the backend enforces this.
 * Stop returns the full VoiceSession including duration and size so the
 * caller can decide what to do with it (persist, process, discard).
 *
 * Later phases will layer diarization + transcription on top. This hook
 * stays focused on the capture lifecycle.
 */
import { useCallback, useRef, useState } from "react";
import { invoke } from "@tauri-apps/api/core";

import type { VoiceSession } from "@/types/voice";

interface UseVoiceSessionResult {
  current: VoiceSession | null;
  isRecording: boolean;
  elapsedSeconds: number;
  error: string | null;
  start: () => Promise<VoiceSession | null>;
  stop: () => Promise<VoiceSession | null>;
}

const isNative =
  typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;

export function useVoiceSession(): UseVoiceSessionResult {
  const [current, setCurrent] = useState<VoiceSession | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const startedAtRef = useRef<number | null>(null);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const start = useCallback(async (): Promise<VoiceSession | null> => {
    setError(null);
    if (!isNative) {
      setError("Session recording is only available on iOS.");
      return null;
    }
    try {
      const session = await invoke<VoiceSession>("plugin:voice|start_session");
      setCurrent(session);
      setIsRecording(true);
      startedAtRef.current = Date.now();
      setElapsedSeconds(0);
      tickRef.current = setInterval(() => {
        if (startedAtRef.current !== null) {
          setElapsedSeconds(Math.floor((Date.now() - startedAtRef.current) / 1000));
        }
      }, 1000);
      return session;
    } catch (err) {
      setError(String(err));
      return null;
    }
  }, []);

  const stop = useCallback(async (): Promise<VoiceSession | null> => {
    if (!isNative) return null;
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
    try {
      const session = await invoke<VoiceSession>("plugin:voice|stop_session");
      setCurrent(session);
      setIsRecording(false);
      startedAtRef.current = null;
      return session;
    } catch (err) {
      setError(String(err));
      setIsRecording(false);
      startedAtRef.current = null;
      return null;
    }
  }, []);

  return { current, isRecording, elapsedSeconds, error, start, stop };
}
